/**
 * OverbookingLimitsSection
 * ──────────────────────────────────────────────────────────────────────
 * Settings section that renders overbooking limits by location + provider
 * with AI recommendations (Edit 6 of scheduler_edits_v2.md).
 *
 * Props:
 *   locations:   [{ id, name, providers: [{ id, name, limit, aiSuggested, aiRationale,
 *                                           weekOverbooked, weekNoShows, autoAdjust }],
 *                  locationCap }]
 *   approvalMode: 'manager' | 'auto_75' | 'block_all'
 *   readOnly:    boolean — disables mutation in non-admin contexts
 *   onChangeLimit(locationId, providerId, nextLimit)
 *   onApplyAi(locationId, providerId)
 *   onToggleAuto(locationId, providerId)
 *   onChangeLocationCap(locationId, nextCap)
 *   onChangeApprovalMode(mode)
 *
 * Pure UI from props — the caller owns state (demo fixtures vs real API).
 */
import { useState } from 'react';
import { cn } from '../../../../lib/utils';
import { Button } from '../../../ui/button';
import { ChevronDown, ChevronUp, Sparkles, AlertTriangle, CheckCircle2 } from 'lucide-react';

const Stepper = ({ value, min = 0, max = 10, onChange, disabled, testId }) => (
  <div className="flex items-center gap-1" data-testid={testId}>
    <button
      onClick={() => !disabled && onChange(Math.max(min, (value || 0) - 1))}
      className="flex items-center justify-center"
      style={{
        width: 28,
        height: 28,
        background: '#f5f5f5',
        border: '0.5px solid #ebebeb',
        borderRadius: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
      data-testid={`${testId}-dec`}
      aria-label="Decrease"
    >
      −
    </button>
    <span
      className="text-center"
      data-testid={`${testId}-value`}
      style={{
        fontSize: 18,
        fontWeight: 500,
        color: '#111',
        minWidth: 32,
      }}
    >
      {value}
    </span>
    <button
      onClick={() => !disabled && onChange(Math.min(max, (value || 0) + 1))}
      className="flex items-center justify-center"
      style={{
        width: 28,
        height: 28,
        background: '#f5f5f5',
        border: '0.5px solid #ebebeb',
        borderRadius: 6,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
      }}
      data-testid={`${testId}-inc`}
      aria-label="Increase"
    >
      +
    </button>
  </div>
);

const ProviderCard = ({ providerId, data, locationId, readOnly, onChangeLimit, onApplyAi, onToggleAuto, onChangeProviderConfig }) => {
  const matches = data.limit === data.aiSuggested;
  const aiLower = data.aiSuggested < data.limit;
  const providerConfig = data.config || {};
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-4 space-y-3"
      data-testid={`overbook-provider-${providerId}`}
    >
      <div className="text-sm font-medium text-slate-900">{data.name}</div>
      <div className="flex items-center gap-3">
        <span className="text-[10px] text-slate-500 w-48 shrink-0">
          Max overbooked patients per day:
        </span>
        <Stepper
          value={data.limit}
          onChange={(v) => onChangeLimit(locationId, providerId, v)}
          disabled={readOnly || data.autoAdjust}
          testId={`overbook-stepper-${providerId}`}
        />
        {data.autoAdjust && (
          <span className="text-[10px] text-slate-500 italic">
            Auto-managed by Echo AI
          </span>
        )}
      </div>

      <div className="rounded-md bg-slate-50 p-2 text-[11px] space-y-1">
        <div className="flex items-center gap-1.5 font-medium">
          <Sparkles className="h-3 w-3 text-violet-500" />
          {matches ? (
            <span className="text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Setting matches AI recommendation
            </span>
          ) : (
            <span className={cn(aiLower ? 'text-amber-700' : 'text-slate-700')}>
              AI recommendation: {data.aiSuggested}
              {aiLower && (
                <AlertTriangle className="h-3 w-3 text-amber-500 inline-block ml-1" />
              )}
            </span>
          )}
        </div>
        <p className="text-slate-600">{data.aiRationale}</p>
        {!matches && (
          <div className="flex gap-2 pt-1">
            <Button
              size="sm"
              className="h-6 text-[10px] bg-[#6b5ce7] hover:bg-[#5a4bd6]"
              onClick={() => onApplyAi(locationId, providerId)}
              disabled={readOnly}
              data-testid={`overbook-apply-ai-${providerId}`}
            >
              Apply AI suggestion
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="h-6 text-[10px]"
              data-testid={`overbook-keep-${providerId}`}
            >
              Keep my setting
            </Button>
          </div>
        )}
      </div>

      <div className="text-[10px] text-slate-500">
        Current week: {data.weekOverbooked} overbooked · {data.weekNoShows} no-show
        {data.weekNoShows === 1 ? '' : 's'} from OB
      </div>

      <label
        className="flex items-center gap-2 text-[10px] text-slate-600"
        data-testid={`overbook-auto-${providerId}`}
      >
        <input
          type="checkbox"
          checked={!!data.autoAdjust}
          onChange={() => onToggleAuto(locationId, providerId)}
          disabled={readOnly}
        />
        Let Echo AI auto-adjust this limit weekly
      </label>

      {/* Engine v1.0 controls — overbook window, auto-contact, collision protocol, goodwill */}
      {onChangeProviderConfig && (
        <div className="pt-2 border-t border-slate-100 space-y-2" data-testid={`overbook-engine-${providerId}`}>
          <div className="text-[9px] uppercase text-slate-400 font-semibold">Engine behaviour</div>

          <div className="grid grid-cols-2 gap-2 text-[11px]">
            <label className="flex flex-col gap-0.5">
              <span className="text-slate-500 text-[10px]">Overbook window</span>
              <select
                value={providerConfig.overbookWindow || 'all_day'}
                onChange={(e) => onChangeProviderConfig(providerId, 'overbookWindow', e.target.value)}
                className="px-2 py-1.5 text-[11px] outline-none"
                style={{ background: '#f9f9f9', border: '0.5px solid #ebebeb', borderRadius: 6 }}
                disabled={readOnly}
                data-testid={`overbook-window-${providerId}`}
              >
                <option value="morning_only">Morning only</option>
                <option value="all_day">All day</option>
                <option value="afternoon_only">Afternoon only</option>
                <option value="custom">Custom</option>
              </select>
            </label>

            <label className="flex flex-col gap-0.5">
              <span className="text-slate-500 text-[10px]">Collision protocol</span>
              <select
                value={providerConfig.collisionProtocol || 'offer_next_slot'}
                onChange={(e) => onChangeProviderConfig(providerId, 'collisionProtocol', e.target.value)}
                className="px-2 py-1.5 text-[11px] outline-none"
                style={{ background: '#f9f9f9', border: '0.5px solid #ebebeb', borderRadius: 6 }}
                disabled={readOnly}
                data-testid={`overbook-collision-${providerId}`}
              >
                <option value="offer_next_slot">Offer next slot</option>
                <option value="offer_goodwill">Offer next slot + goodwill</option>
                <option value="manual_only">Manual only</option>
              </select>
            </label>
          </div>

          <label className="flex items-center gap-2 text-[10px] text-slate-600">
            <input
              type="checkbox"
              checked={!!providerConfig.autoContactWaitlist}
              onChange={() => onChangeProviderConfig(providerId, 'autoContactWaitlist', !providerConfig.autoContactWaitlist)}
              disabled={readOnly}
              data-testid={`overbook-autocontact-${providerId}`}
            />
            Echo AI auto-contacts the waitlist (no staff action required)
          </label>

          {providerConfig.collisionProtocol === 'offer_goodwill' && (
            <label className="flex items-center gap-2 text-[10px] text-slate-600">
              <span className="w-32 shrink-0">Goodwill credit</span>
              <select
                value={String(providerConfig.goodwillAmount ?? 0)}
                onChange={(e) => onChangeProviderConfig(providerId, 'goodwillAmount', parseInt(e.target.value, 10))}
                className="px-2 py-1 text-[11px] outline-none"
                style={{ background: '#f9f9f9', border: '0.5px solid #ebebeb', borderRadius: 6 }}
                disabled={readOnly}
                data-testid={`overbook-goodwill-${providerId}`}
              >
                <option value="0">$0</option>
                <option value="10">$10</option>
                <option value="25">$25</option>
              </select>
            </label>
          )}
        </div>
      )}
    </div>
  );
};

const OverbookingLimitsSection = ({
  locations = [],
  approvalMode = 'manager',
  readOnly = false,
  globalRules = null, // { riskThreshold, neverOverbook, staffConfirmation }
  onChangeLimit = () => {},
  onApplyAi = () => {},
  onToggleAuto = () => {},
  onChangeLocationCap = () => {},
  onChangeApprovalMode = () => {},
  onChangeProviderConfig = null,
  onChangeGlobalRules = null,
}) => {
  const [openIds, setOpenIds] = useState(() => new Set(locations.map((l) => l.id)));
  const toggleLocation = (id) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4" data-testid="overbooking-section">
      <div>
        <p className="text-sm font-semibold text-slate-800">Overbooking limits</p>
        <p className="text-[11px] text-slate-500 mt-0.5">
          Set how many additional patients can be booked beyond a provider&apos;s full schedule for
          each location. Echo AI can suggest adjustments based on historical show rates.
        </p>
      </div>

      {locations.length === 0 && (
        <div className="py-6 text-center rounded-lg border border-dashed border-slate-200 text-[11px] text-slate-500">
          No locations configured yet.
        </div>
      )}

      {locations.map((loc) => {
        const expanded = openIds.has(loc.id);
        return (
          <div
            key={loc.id}
            className="rounded-lg border border-slate-200 bg-white"
            data-testid={`overbook-location-${loc.id}`}
          >
            <button
              onClick={() => toggleLocation(loc.id)}
              className="w-full flex items-center gap-2 px-4 py-3 text-left"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              data-testid={`overbook-location-toggle-${loc.id}`}
            >
              {expanded ? (
                <ChevronDown className="h-4 w-4 text-slate-500" />
              ) : (
                <ChevronUp className="h-4 w-4 text-slate-500 rotate-180" />
              )}
              <span className="text-sm font-medium text-slate-900">{loc.name}</span>
              <span className="text-[10px] text-slate-400 ml-auto">
                {loc.providers?.length || 0} providers
              </span>
            </button>

            {expanded && (
              <div className="px-4 pb-4 space-y-3 border-t border-slate-100">
                <p className="text-[10px] uppercase text-slate-400 font-semibold pt-3">
                  Per-provider limits
                </p>
                {(loc.providers || []).map((p) => (
                  <ProviderCard
                    key={p.id}
                    providerId={p.id}
                    locationId={loc.id}
                    data={p}
                    readOnly={readOnly}
                    onChangeLimit={onChangeLimit}
                    onApplyAi={onApplyAi}
                    onToggleAuto={onToggleAuto}
                    onChangeProviderConfig={onChangeProviderConfig}
                  />
                ))}

                <div className="pt-2 border-t border-slate-100 space-y-1">
                  <p className="text-[10px] uppercase text-slate-400 font-semibold">
                    Location-wide cap (optional)
                  </p>
                  <div className="flex items-center gap-2 text-[11px] text-slate-600">
                    <span>Never overbook more than</span>
                    <Stepper
                      value={loc.locationCap || 0}
                      onChange={(v) => onChangeLocationCap(loc.id, v)}
                      disabled={readOnly}
                      testId={`overbook-loc-cap-${loc.id}`}
                    />
                    <span>total patients per day at this location.</span>
                  </div>
                  <p className="text-[9px] text-slate-400">
                    Leave at 0 to disable location-wide cap.
                  </p>
                </div>
              </div>
            )}
          </div>
        );
      })}

      {/* Approval workflow */}
      <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-2" data-testid="overbook-approval-mode">
        <p className="text-[11px] font-medium text-slate-900">
          When overbooking limit is reached:
        </p>
        {[
          { id: 'manager', label: 'Require manager approval before booking (recommended)' },
          { id: 'auto_75', label: 'Auto-approve if Echo AI show probability is ≥ 75%' },
          { id: 'block_all', label: 'Block all overbooking — no exceptions' },
        ].map((opt) => (
          <label
            key={opt.id}
            className="flex items-center gap-2 text-[11px] text-slate-700"
            data-testid={`overbook-approval-${opt.id}`}
          >
            <input
              type="radio"
              name="overbook-approval-mode"
              value={opt.id}
              checked={approvalMode === opt.id}
              onChange={() => onChangeApprovalMode(opt.id)}
              disabled={readOnly}
            />
            {opt.label}
          </label>
        ))}
        <p className="text-[9px] text-slate-400 pt-1">
          Manager approval requests are sent as in-app alerts and must be resolved within 30 minutes
          or Echo will auto-decline.
        </p>
      </div>

      {/* Risk threshold + global rules + staff confirmation (Engine v1.0) */}
      {globalRules && onChangeGlobalRules && (
        <div className="rounded-lg border border-slate-200 bg-white p-4 space-y-3" data-testid="overbook-global-rules">
          <div>
            <p className="text-[11px] font-medium text-slate-900">Risk threshold for flagging</p>
            <p className="text-[9px] text-slate-500">
              Slots above this risk score are flagged for overbook candidate selection. Echo
              recommends 30% for most practices.
            </p>
            <select
              value={String(globalRules.riskThreshold)}
              onChange={(e) => onChangeGlobalRules({ ...globalRules, riskThreshold: parseInt(e.target.value, 10) })}
              className="mt-1.5 px-2 py-1.5 text-[11px] outline-none"
              style={{ background: '#f9f9f9', border: '0.5px solid #ebebeb', borderRadius: 6, width: 240 }}
              disabled={readOnly}
              data-testid="overbook-risk-threshold"
            >
              <option value="20">20% (aggressive)</option>
              <option value="30">30% (balanced, recommended)</option>
              <option value="40">40% (conservative)</option>
              <option value="50">50% (minimal)</option>
            </select>
          </div>

          <div className="pt-2 border-t border-slate-100">
            <p className="text-[11px] font-medium text-slate-900 mb-1.5">Never overbook</p>
            {[
              { key: 'newPatient', label: 'New patient appointments (recommended)' },
              { key: 'procedure', label: 'Procedure / surgical slots' },
              { key: 'extendedAbsence', label: 'First visit after extended absence (>12 months)' },
              { key: 'confirmedRecent', label: 'If original patient confirmed within last 4 hours (recommended)' },
            ].map((rule) => (
              <label
                key={rule.key}
                className="flex items-center gap-2 text-[11px] text-slate-700"
                data-testid={`overbook-never-${rule.key}`}
              >
                <input
                  type="checkbox"
                  checked={!!globalRules.neverOverbook?.[rule.key]}
                  onChange={() => onChangeGlobalRules({
                    ...globalRules,
                    neverOverbook: {
                      ...globalRules.neverOverbook,
                      [rule.key]: !globalRules.neverOverbook?.[rule.key],
                    },
                  })}
                  disabled={readOnly}
                />
                {rule.label}
              </label>
            ))}
          </div>

          <div className="pt-2 border-t border-slate-100">
            <p className="text-[11px] font-medium text-slate-900 mb-1.5">
              Require staff confirmation before Echo contacts a waitlist patient
            </p>
            {[
              { id: 'always', label: 'Always' },
              { id: 'only_above_limit', label: 'Only when above daily limit (recommended)' },
              { id: 'never', label: 'Never — Echo handles fully autonomously' },
            ].map((opt) => (
              <label
                key={opt.id}
                className="flex items-center gap-2 text-[11px] text-slate-700"
                data-testid={`overbook-staffconfirm-${opt.id}`}
              >
                <input
                  type="radio"
                  name="overbook-staff-confirmation"
                  value={opt.id}
                  checked={globalRules.staffConfirmation === opt.id}
                  onChange={() => onChangeGlobalRules({ ...globalRules, staffConfirmation: opt.id })}
                  disabled={readOnly}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default OverbookingLimitsSection;
